<template>
  <div class="home">
    <!-- Navigation -->
    <AppNavbar active-tab="Home" theme="dark" />

    <!-- Section 1: Hero -->
    <section class="section hero">
      <div class="hero-content">
        <h1 class="hero-title">More than a pen</h1>
        <p class="hero-desc">
          It's a note-taker, a thought organizer,<br />
          and an emotion interpreter
        </p>
        <button class="btn-learn-more">Learn more</button>
      </div>
    </section>

    <!-- Section 2: Smart core & Precise sensor -->
    <section class="section smart-core">
      <div class="smart-content">
        <h2 class="section-heading">
          Smart core &amp;<br />Precise sensor
        </h2>
        <div class="tag-group">
          <span class="tag">Stroke Order and Trajectory</span>
          <span class="tag">Writing Speed</span>
          <span class="tag">Pressure</span>
        </div>
        <p class="smart-desc">
          The pen is equipped with an advanced high-speed<br />
          camera, which captures every subtle detail during<br />
          the writing process in real time.
        </p>
      </div>
    </section>

    <!-- Section 3: Multimodal Input -->
    <section class="section multimodal">
      <h2 class="section-heading center">Multimodal Input</h2>
      <p class="section-subtitle center">
        Bring together what you write, hear, think, and more importantly, what you feel.
      </p>
      <div class="multimodal-body">
        <div class="multimodal-image">
          <img :src="multimodalImg" alt="Multimodal Input" />
        </div>
        <div class="multimodal-features">
          <div class="feature-item active">
            <h3 class="feature-title">Handwriting and voice are recorded simultaneously</h3>
            <p class="feature-desc">Stay fully engaged in meetings and classes.</p>
          </div>
          <div class="feature-item">
            <h3 class="feature-title">Dual AI Analysis of Handwriting and Audio</h3>
            <p class="feature-desc">Enhanced accuracy aligned with your thought patterns.</p>
          </div>
          <div class="feature-item">
            <h3 class="feature-title">AI Handwriting Analysis Powered by Models</h3>
            <p class="feature-desc">
              Gain insights into your stress, energy, and emotional state
              through analyzing your writing characteristics and process.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 4: Our users -->
    <section class="section our-users">
      <h2 class="section-heading center">Our users</h2>
      <p class="section-subtitle center">
        We provide writing and psychological healing solutions for professionals and students across diverse industries.
      </p>
      <div class="user-cards">
        <div
          class="user-card"
          v-for="(user, idx) in users"
          :key="idx"
          :class="{ 'user-card--placeholder': !user.img }"
        >
          <img v-if="user.img" :src="user.img" :alt="user.label" />
          <template v-else>
            <div class="user-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
              </svg>
            </div>
            <!-- <span class="user-label">{{ user.label }}</span> -->
          </template>
        </div>
      </div>
      <button class="btn-preorder">Pre-order Now</button>
    </section>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script>
import AppNavbar from '@/components/AppNavbar.vue'
import AppFooter from '@/components/AppFooter.vue'

export default {
  name: 'Home',
  components: { AppNavbar, AppFooter },
  data() {
    return {
      multimodalImg: require('@/assets/home/recording-tip.png'),
      users: [
        { label: 'Bussiness man', img: require('@/assets/home/user-card1.png') },
        { label: 'Company Staff', img: require('@/assets/home/user-card2.png') },
        { label: 'Insurance agent', img: require('@/assets/home/user-card3.png') },
        { label: 'Collage Student', img: require('@/assets/home/user-card4.png') },
        { label: 'More', img: require('@/assets/home/user-card5.png') }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
// Colors
$green-accent: #5cf2a0;
$text-white: #ffffff;
$text-gray: #b8b8c8;
$text-muted: #6b6b80;

// Layout
$container-max: 1760px;
$container-padding: clamp(24px, 14.79vw, 284px);

// Responsive breakpoints
// Large desktop: >= 1200px (default)
// Tablet: 768px - 1199px
// Mobile: < 768px

.home {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

// ========== Shared Section ==========
.section {
  width: 100%;
  position: relative;
}

.section-heading {
  font-size: clamp(28px, 3.5vw, 44px);
  font-weight: 700;
  line-height: 1.3;
  color: $green-accent;
  margin: 0;

  &.center {
    text-align: center;
  }
}

.section-subtitle {
  font-size: clamp(15px, 1.5vw, 20px);
  color: $text-gray;
  margin-top: 16px;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  &.center {
    text-align: center;
  }
}

// ========== Section 1: Hero ==========
.hero {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: url('~@/assets/home/homePage1.png') center center / cover no-repeat;
  display: flex;
  align-items: center;
  padding: 120px $container-padding 80px;

  .hero-content {
    max-width: $container-max;
    margin: 0 auto;
    width: 100%;
  }

  .hero-title {
    font-size: clamp(40px, 5.5vw, 72px);
    font-weight: 800;
    color: $text-white;
    margin: 0 0 24px;
    line-height: 1.1;
    letter-spacing: -1px;
  }

  .hero-desc {
    font-size: clamp(16px, 1.7vw, 22px);
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.6;
    margin: 0 0 40px;
  }

  .btn-learn-more {
    display: inline-block;
    padding: 12px 28px;
    border: 1.5px solid rgba(255, 255, 255, 0.6);
    border-radius: 30px;
    background: transparent;
    color: $text-white;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #fff;
    }
  }
}

// ========== Section 2: Smart core ==========
.smart-core {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: url('~@/assets/home/homePage2.png') center center / cover no-repeat;
  display: flex;
  align-items: center;
  padding: 120px $container-padding 80px;

  .smart-content {
    max-width: $container-max;
    margin: 0 auto;
    width: 100%;
  }

  .tag-group {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 28px 0;
    max-width: 520px;
  }

  .tag {
    display: inline-block;
    padding: 8px 18px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 20px;
    color: rgba(255, 255, 255, 0.85);
    font-size: clamp(13px, 1.2vw, 15px);
    white-space: nowrap;
  }

  .smart-desc {
    font-size: clamp(16px, 1.6vw, 20px);
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.6;
    margin: 0;
    max-width: 520px;
  }
}

// ========== Section 3: Multimodal Input ==========
.multimodal {
  background: linear-gradient(180deg, #0d0818 0%, #110c20 100%);
  padding: 80px $container-padding 100px;

  .multimodal-body {
    max-width: $container-max;
    margin: 60px auto 0;
    display: flex;
    align-items: center;
    gap: 60px;
  }

  .multimodal-image {
    flex: 0 0 40%;
    max-width: 480px;

    img {
      width: 100%;
      height: auto;
      border-radius: 16px;
      display: block;
    }
  }

  .multimodal-features {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 36px;
    border-left: 1px solid rgba(255, 255, 255, 0.15);
    padding-left: 40px;
  }

  .feature-item {
    .feature-title {
      font-size: clamp(18px, 1.8vw, 24px);
      font-weight: 600;
      margin: 0 0 10px;
      color: $text-muted;
      line-height: 1.4;
    }

    .feature-desc {
      font-size: clamp(14px, 1.4vw, 18px);
      color: $text-muted;
      margin: 0;
      line-height: 1.5;
      opacity: 0.7;
    }

    &.active {
      .feature-title {
        color: #f0d9b5;
      }

      .feature-desc {
        color: $text-gray;
        opacity: 1;
      }
    }
  }
}

// ========== Section 4: Our users ==========
.our-users {
  background: linear-gradient(180deg, #110c20 0%, #1a1030 50%, #0f0a1c 100%);
  padding: 80px $container-padding 70px;

  .user-cards {
    max-width: $container-max;
    margin: 60px auto 0;
    display: flex;
    gap: 20px;
    justify-content: center;
  }

  .user-card {
    position: relative;
    flex: 0 0 calc((100% - 80px) / 5);
    aspect-ratio: 240 / 520;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s;

    &:hover {
      transform: translateY(-6px);
    }

    &--placeholder {
      background: linear-gradient(160deg, #2d1f4a 0%, #1a1030 100%);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .user-placeholder {
      color: rgba(255, 255, 255, 0.35);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .user-label {
      position: absolute;
      bottom: 24px;
      left: 24px;
      color: #fff;
      font-size: clamp(16px, 1.5vw, 24px);
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    }
  }

  .btn-preorder {
    display: block;
    margin: 50px auto 0;
    padding: 16px 56px;
    border: none;
    border-radius: 35px;
    background: linear-gradient(90deg, #4ade80 0%, #7c3aed 100%);
    color: #fff;
    font-size: clamp(16px, 1.5vw, 20px);
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.3s, transform 0.3s;

    &:hover {
      opacity: 0.9;
      transform: translateY(-2px);
    }
  }
}

// ========== Responsive: Tablet ==========
@media (max-width: 1024px) {
  .multimodal {
    .multimodal-body {
      gap: 40px;
    }
    .multimodal-features {
      padding-left: 30px;
      gap: 28px;
    }
  }

  .our-users {
    .user-cards {
      gap: 16px;
    }
    .user-card {
      flex: 0 0 calc((100% - 64px) / 5);
      .user-label {
        bottom: 16px;
        left: 16px;
      }
    }
  }
}

// ========== Responsive: Mobile ==========
@media (max-width: 768px) {
  .hero,
  .smart-core {
    padding-top: 90px;
    padding-bottom: 50px;
  }

  .hero {
    .hero-title {
      margin-bottom: 16px;
    }
    .hero-desc {
      margin-bottom: 28px;
      br {
        display: none;
      }
    }
  }

  .smart-core {
    .tag-group {
      margin: 20px 0;
      max-width: 100%;
    }
    .tag {
      padding: 6px 14px;
    }
    .smart-desc {
      max-width: 100%;
      br {
        display: none;
      }
    }
  }

  .multimodal {
    padding: 50px 0 60px;
    .multimodal-body {
      flex-direction: column;
      gap: 40px;
      margin-top: 40px;
    }
    .multimodal-image {
      flex: 0 0 auto;
      max-width: 100%;
      width: 100%;
      max-width: 420px;
      margin: 0 auto;
    }
    .multimodal-features {
      border-left: none;
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      padding-left: 0;
      padding-top: 30px;
      gap: 24px;
    }
  }

  .our-users {
    padding: 50px 0 50px;
    .user-cards {
      margin-top: 40px;
      gap: 12px;
      flex-wrap: wrap;
    }
    .user-card {
      flex: 0 0 calc((100% - 12px) / 2);
      max-width: none;
      border-radius: 12px;
      .user-label {
        bottom: 12px;
        left: 12px;
        font-size: 16px;
      }
    }
    .btn-preorder {
      margin-top: 36px;
      padding: 14px 40px;
    }
  }
}

// ========== Responsive: Small Mobile ==========
@media (max-width: 480px) {
  .our-users {
    .user-card {
      flex: 1 1 100%;
      max-width: 280px;
      margin: 0 auto;
    }
  }
}
</style>
